import React, { FC, useEffect, useState, useMemo } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import { FiExternalLink } from 'react-icons/fi';

import { fetchDocumentById } from '../../services/fileService';
import { CloseIcon } from '../icons';
import PdfViewer from '../viewers/PdfViewer';
import DocxViewer from './DocxViewer';
import XlsxViewer from './XlsxViewer';

interface DocumentIframeViewerProps {
    documentId: string;
    documentName: string;
    onClose: () => void;
}

const isPdfContent = (content: string, fileName: string): boolean => {
    // Check by file extension
    if (fileName.toLowerCase().endsWith('.pdf')) {
        return true;
    }
    // Check by content (blob URL, data URL, or base64 PDF signature)
    if (content.startsWith('data:application/pdf')) {
        return true;
    }
    // Check for PDF base64 signature (JVBERi0 = %PDF-)
    if (content.startsWith('JVBERi0')) {
        return true;
    }
    return false;
};

// Mirrors DocumentViewer's isDocxContent/isLegacyDocContent: .doc and .docx both go through DocxViewer
const isWordContent = (fileName: string, contentType: string): boolean => {
    const ln = fileName.toLowerCase();
    const ct = contentType.toLowerCase();
    if (ln.endsWith('.docx') || ln.endsWith('.doc')) return true;
    return ct.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') || ct.includes('application/msword');
};

const isLegacyWordContent = (fileName: string, contentType: string): boolean => {
    const ln = fileName.toLowerCase();
    if (ln.endsWith('.doc')) return true;
    if (ln.endsWith('.docx')) return false;
    return contentType.toLowerCase().includes('application/msword');
};

// Mirrors DocumentViewer's isXlsxFile: .xls and .xlsx both go through XlsxViewer
const isSpreadsheetContent = (fileName: string, contentType: string): boolean => {
    const ln = fileName.toLowerCase();
    const ct = contentType.toLowerCase();
    if (ln.endsWith('.xlsx') || ln.endsWith('.xls')) return true;
    return ct.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || ct.includes('application/vnd.ms-excel');
};

// Types the browser can display in a tab; anything else would be downloaded under the blob UUID (no extension)
const canBrowserRenderInline = (contentType: string, isPdf: boolean): boolean => {
    if (isPdf) return true;
    const ct = contentType.split(';')[0].trim().toLowerCase();
    return (
        ct.startsWith('image/') ||
        ct.startsWith('text/') ||
        ct.startsWith('video/') ||
        ct.startsWith('audio/') ||
        ct === 'application/json' ||
        ct === 'application/xml' ||
        ct === 'application/xhtml+xml'
    );
};

// A blob: URL carries no filename, so the `download` attribute is the only way to keep the name/extension
const downloadWithName = (url: string, fileName: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export const DocumentIframeViewer: FC<DocumentIframeViewerProps> = observer(({ documentId, documentName, onClose }) => {
    const [isDocumentFetching, setIsDocumentFetching] = useState(false);
    const [documentLink, setDocumentLink] = useState<string | null>(null);
    const [documentContentType, setDocumentContentType] = useState('');

    const OpenInNewIcon = FiExternalLink as any;

    // Determine if document is PDF
    const isPdf = useMemo(() => {
        if (!documentLink) return false;
        return isPdfContent(documentLink, documentName);
    }, [documentLink, documentName]);

    const isWord = useMemo(() => isWordContent(documentName, documentContentType), [documentName, documentContentType]);
    const isLegacyWord = useMemo(() => isLegacyWordContent(documentName, documentContentType), [documentName, documentContentType]);
    const isSpreadsheet = useMemo(() => isSpreadsheetContent(documentName, documentContentType), [documentName, documentContentType]);

    // Fetch document when documentId changes
    useEffect(() => {
        if (!documentId) {
            setDocumentLink(null);
            setDocumentContentType('');
            return;
        }

        const fetchDocument = async () => {
            setIsDocumentFetching(true);
            setDocumentLink(null);
            setDocumentContentType('');

            try {
                const result = await fetchDocumentById(documentId, documentName);
                
                if (result.success && result.content) {
                    setDocumentLink(result.content);
                    setDocumentContentType(result.contentType || '');
                } else {
                    setDocumentLink(null);
                }
            } catch {
                setDocumentLink(null);
            } finally {
                setIsDocumentFetching(false);
            }
        };

        fetchDocument();
    }, [documentId]);

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', borderTop: '1px solid', borderColor: 'divider' }}>
            {/* Header with document name and action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }} id='header'>
                <Typography 
                    variant='body2' 
                    sx={{ 
                        fontWeight: 500, 
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        mr: 1,
                        fontSize: '16px'
                    }}
                    title={documentName}
                >
                    {documentName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {documentLink && (
                        <Tooltip title="Open in new tab">
                            <IconButton 
                                onClick={() => {
                                    if (canBrowserRenderInline(documentContentType, isPdf)) {
                                        window.open(documentLink, '_blank');
                                    } else {
                                        downloadWithName(documentLink, documentName);
                                    }
                                }}
                                size="small"
                                sx={{ marginRight: 0.5 }}
                            >
                                <OpenInNewIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Close">
                        <IconButton 
                            onClick={onClose} 
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            
            {/* Document content */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1 }}>
                {isDocumentFetching ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={27} />
                    </Box>
                ) : documentLink ? (
                    isPdf ? (
                        <PdfViewer content={documentLink} fileName={documentName} />
                    ) : isSpreadsheet ? (
                        <Box sx={{ height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <XlsxViewer source={documentLink} fileName={documentName} />
                        </Box>
                    ) : isWord ? (
                        <Box sx={{ height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <DocxViewer source={documentLink} fileName={documentName} legacyDoc={isLegacyWord} />
                        </Box>
                    ) : (
                        <iframe 
                            src={documentLink} 
                            width="100%" 
                            height="100%" 
                            style={{ border: 'none' }}
                        />
                    )
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant='body1' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            No document found!
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

